import type { JourneyNode, NodeType } from '../../entities/node';

export function normalizeIdList(ids: string[] | undefined): string[] { return [...new Set((ids ?? []).filter(Boolean))].sort(); }
export function pairKey(a: string, b: string): string { return [a, b].sort().join('::'); }

export function canonicalizeNodeReferences(node: JourneyNode, nodes: JourneyNode[]): JourneyNode {
	const byId = new Map<string, JourneyNode>();
	const byName = new Map<string, JourneyNode[]>();

	for (const candidate of nodes) {
		byId.set(normalizeReferenceToken(candidate.nodeId), candidate);
		if (!candidate.displayName.trim()) continue;
		const key = `${candidate.type}::${normalizeReferenceToken(candidate.displayName)}`;
		byName.set(key, [...(byName.get(key) ?? []), candidate]);
	}

	if (node.type === 'Human') {
		return {
			...node,
			occupationIds: canonicalizeReferenceList(node.occupationIds, 'Occupation', byId, byName),
			projectIds: canonicalizeReferenceList(node.projectIds, 'Project', byId, byName),
			relatedHumanIds: canonicalizeReferenceList(node.relatedHumanIds, 'Human', byId, byName),
		};
	}

	if (node.type === 'Project') {
		return {
			...node,
			participantHumanIds: canonicalizeReferenceList(node.participantHumanIds, 'Human', byId, byName),
			relatedOccupationIds: canonicalizeReferenceList(node.relatedOccupationIds, 'Occupation', byId, byName),
			relatedProjectIds: canonicalizeReferenceList(node.relatedProjectIds, 'Project', byId, byName),
		};
	}

	return {
		...node,
		relatedHumanIds: canonicalizeReferenceList(node.relatedHumanIds, 'Human', byId, byName),
		typicalProjectIds: canonicalizeReferenceList(node.typicalProjectIds, 'Project', byId, byName),
	};
}

function canonicalizeReferenceList(
	values: string[],
	expectedType: NodeType,
	byId: Map<string, JourneyNode>,
	byName: Map<string, JourneyNode[]>
): string[] {
	return [...new Set(values.map((value) => resolveReferenceToken(value, expectedType, byId, byName)).filter(Boolean))].sort((left, right) => left.localeCompare(right));
}

function resolveReferenceToken(
	value: string,
	expectedType: NodeType,
	byId: Map<string, JourneyNode>,
	byName: Map<string, JourneyNode[]>
): string {
	const token = value.trim();
	if (!token) {
		return '';
	}

	const normalized = normalizeReferenceToken(token);
	const directMatch = byId.get(normalized);
	if (directMatch?.type === expectedType) {
		return directMatch.nodeId;
	}

	const nameMatches = byName.get(`${expectedType}::${normalized}`) ?? [];
	if (nameMatches.length === 1) {
		return nameMatches[0].nodeId;
	}

	return token;
}

function normalizeReferenceToken(value: string): string {
	return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}
